import { Unsubscribe } from 'firebase/firestore';
import {
  getMessaging,
  isSupported,
  MessagePayload,
  Messaging,
  onMessage,
} from 'firebase/messaging';
import { getErrorReporter } from 'shared/services/error-tracking';

import { initApp } from './init-app';

let messaging: Messaging | undefined;
let unsubscribeMessage: Unsubscribe | undefined;

const isIllegalConstructor = (error: unknown) => error instanceof TypeError;

// constructor first: it keeps clicks inert; the worker fallback (Android refuses the constructor) routes clicks to the SW handler
export const showForegroundNotification = async (payload: MessagePayload) => {
  const title = payload.notification?.title ?? 'Griply';
  const options = {
    body: payload.notification?.body,
    icon: '/favicon/mstile-150x150.png',
  };

  try {
    const notification = new Notification(title, options);
    notification.addEventListener('click', () => notification.close());
    return;
  } catch (error: unknown) {
    if (!isIllegalConstructor(error)) {
      throw error;
    }
  }

  const registration = await window.navigator.serviceWorker?.getRegistration();
  await registration?.showNotification(title, options);
};

const reportNotificationFailure = (error: unknown) =>
  getErrorReporter().captureException(error, {
    tags: { source: 'push-foreground-notification' },
  });

// Undefined where the browser can't do web push: getMessaging's own support check rejects unhandled there.
export const initPushNotifications = async (): Promise<
  Messaging | undefined
> => {
  if (messaging) {
    return messaging;
  }
  if (!(await isSupported())) {
    return undefined;
  }
  // a concurrent call may have created it while this one awaited the support check
  if (!messaging) {
    messaging = getMessaging(initApp());
    unsubscribeMessage = onMessage(messaging, (payload) => {
      showForegroundNotification(payload).catch(reportNotificationFailure);
    });
  }

  return messaging;
};

export const clearPushNotificationsSubscriptions = () => {
  unsubscribeMessage?.();
  messaging = undefined;
};
